import { Button, Col, Form, Modal, notification, Row, Select, Tooltip, Upload } from "antd";
import { useEffect, useRef, useState } from "react";
import { useLazyGetDistrictListQuery, useLazyGetWardListQuery } from "../../../redux/api/SystemApi";
import { FloatInput, FloatTextArea } from '../../common/components/Index'
import { CityResponse, DistrictResponse, fileTypes, WardResponse } from "../../common/types/types";
import { useCheckRegisterEmailMutation, useCheckRegisterIdentificationMutation, useCheckRegisterPhoneMutation, useRegisterAccountMutation } from "../redux/RegisterApi";
import { RegisterAccount } from "../types";
import ReCAPTCHA from "react-google-recaptcha";
import { useAppDispatch, useAppSelector } from "../../../redux/Hooks";
import { toggleRegisterModal } from "../redux/RegisterSlice";
import { patterns } from "../../common/ts/CommonFunctions";
import { useNavigate } from "react-router-dom";
import { helpers } from "../../../assets/js/helper";
import { QuestionCircleFilled } from "@ant-design/icons";
import { profileIDTypes } from "../../profile/types";
import { useUploadFileForObjectMutation, useUploadFileMutation } from "../../../redux/api/FileApi";
import { useLazyVerifyGoogleCaptchaQuery } from "../../../redux/api/PublicApi";

interface RegisterProps {
    onRegisterSuccess: () => void;
    openLogin: () => void;
    province: CityResponse[];
    checkDirty: boolean;
    setCancelVisible: React.Dispatch<React.SetStateAction<boolean>>,
    setCheckDirty: React.Dispatch<React.SetStateAction<boolean>>,
}

const defaultFormValues = {
    orgName: "",
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    province: "",
    ward: "",
    address: "",
    phoneNumber: "",
}

const docTypes = [".doc", ".docx", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".pdf", "application/pdf"];

interface ErrorList {
    [key: string]: string
}

const RegisterOrgForm = ({ onRegisterSuccess, province, openLogin, checkDirty, setCancelVisible, setCheckDirty }: RegisterProps) => {
    const [form] = Form.useForm();
    const [count, setCount] = useState<any>([]);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [wardList, setWardList] = useState<WardResponse[]>([]);
    const [districtList, setDistrictList] = useState<DistrictResponse[]>([]);
    const [deleteIds, setDeleteIds] = useState<number[]>([]);
    const [orgFile, setOrgFile] = useState<any>({});
    const [errorList, setErrorList] = useState<ErrorList>({});

    const [uploadFile] = useUploadFileMutation();
    const [uploadFileForObject] = useUploadFileForObjectMutation();
    const [registerAccount, { isLoading }] = useRegisterAccountMutation();
    const [checkRegisterPhone] = useCheckRegisterPhoneMutation();
    const [checkRegisterEmail] = useCheckRegisterEmailMutation();
    const [checkRegisterIdentification] = useCheckRegisterIdentificationMutation();
    const [districtTrigger] = useLazyGetDistrictListQuery();
    const [wardTrigger] = useLazyGetWardListQuery();
    const [verifyCaptcha] = useLazyVerifyGoogleCaptchaQuery();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const divRef = useRef<HTMLDivElement>(null);
    const userState = useAppSelector(state => state.login);

    useEffect(() => {
        if (form && userState.tempEmail) {
            form.setFieldsValue({
                email: userState.tempEmail
            })
        }

        if (form && checkDirty) {
            let values = form.getFieldsValue();
            let isDirty = false;
            Object.keys(values).forEach(item => {
                if ((values[item] || (typeof (values[item]) === "number"))
                    && values[item] !== defaultFormValues[item as keyof typeof defaultFormValues]) {
                    isDirty = true;
                }
            })

            setCheckDirty(false);

            if (!isDirty) {
                dispatch(toggleRegisterModal());
            } else {
                setCancelVisible(true);
            }
        }
    }, [checkDirty, form, setCheckDirty, setCancelVisible, dispatch, userState.tempEmail])

    const onValuesChange = (changedValues: any) => {
        let key = Object.keys(changedValues)[0];
        let value = changedValues[key];
        if (typeof (value === "string")) {
            setCount({
                ...count,
                [key]: value.length,
            });

            if (errorList && Object.keys(errorList).includes(key)) {
                let newErrorList = { ...errorList };
                delete newErrorList[key];
                setErrorList(newErrorList);
            }
        }

        if (key === "province") {
            if(changedValues.province) {
                districtTrigger({ page: 0, size: 1000, provinceId: changedValues.province }).unwrap().then(res => {
                    setDistrictList([...res.content].sort((a, b) => a.districtName.localeCompare(b.districtName)));
                })
            } else {
                setDistrictList([])
            }
            setWardList([]);
            form.setFieldsValue({
                district: undefined,
                ward: undefined
            })
        }

        if (key === "district") {
            if(changedValues.district) {
                wardTrigger({ page: 0, size: 1000, districtId: changedValues.district }).unwrap().then(res => {
                    setWardList([...res.content].sort((a, b) => a.communeName.localeCompare(b.communeName)));
                })
            } else {
                setWardList([]);
            }
            form.setFieldsValue({
                ward: undefined
            })
        }
    }

    //thiếu số đkkd
    const onFinish = (values: any) => {
        setCheckDirty(false);
        let submitData: RegisterAccount = {
            fullName: values.orgName,
            representativeName: values.name,
            email: values.email,
            password: values.password,
            representativePhone: values.phone,
            roleCode: "ORGANIZATION",
            status: 1,
            businessRegistrationNumber: values.businessRegistrationNumber,
            provinceId: values.province,
            districtId: values.district,
            communeId: values.ward,
            address: values.address,
            phoneNumber: values.orgPhone,
        }

        let isPhoneValid = false;
        let isEmailValid = false;
        let isIdentificationValid = false;
        let newErrorList: ErrorList = {};

        Promise.all([
            checkRegisterPhone(submitData).unwrap(),
            checkRegisterEmail(submitData).unwrap(),
            checkRegisterIdentification(submitData).unwrap()]).then(res => {
                if (res[0] === true) {
                    newErrorList["orgPhone"] = "Số điện thoại đã tồn tại";
                } else if (res[0] === false) {
                    isPhoneValid = true;
                }

                if (res[1] === true) {
                    newErrorList["email"] = "Email đã tồn tại";
                } else if (res[0] === false) {
                    isEmailValid = true;
                }

                if (res[2] === true) {
                    newErrorList["businessRegistrationNumber"] = "Số đăng ký kinh doanh đã tồn tại";
                } else if (res[0] === false) {
                    isIdentificationValid = true;
                }

                if (isPhoneValid && isEmailValid && isIdentificationValid) {
                    registerAccount(submitData).unwrap().then(res => {
                        // notification.success({
                        //     message: "Đăng ký tài khoản thành công",
                        // })
                        // onRegisterSuccess();
                        uploadFileAfterSubmit(res.userId);
                        // navigate(-1)
                    }).catch(err => {
                        notification.error({
                            message: err.data?.message || "Đã có lỗi xảy ra. Vui lòng thử lại sau!"
                        })

                        if (divRef.current) {
                            divRef.current.scrollIntoView({ behavior: 'smooth' });
                        }
                    })
                } else {
                    setErrorList(newErrorList);
                    if (divRef.current) {
                        divRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                }
            })
    }

    const onValidate = (token: string | null) => {
        if (token) {
            verifyCaptcha(token).unwrap().then(res => {
                if (res.success) {
                    setButtonDisabled(false);
                } else {
                    notification.error({ message: "Lỗi Captcha. Vui lòng thử lại sau!" });
                }
            }).catch(err => {
                console.log(err);
                notification.error({ message: "Lỗi Captcha. Vui lòng thử lại sau!" });
            })
        } else {
            setButtonDisabled(true);
        }
    }

    const checkEmptyInput = (_: any, value: string, massage: string) => {
        if (!value?.trim()) {
            return Promise.reject(new Error(massage))
        }

        return Promise.resolve();
    }

    const onFileRemove = (file: any) => {
        return new Promise<boolean>((resolve, reject) => {
            Modal.confirm({
                className: "custom-modal",
                title: 'Xoá file đã chọn?',
                onOk: () => {
                    if (file.response?.attachmentId) {
                        let newDeleteIds = deleteIds;
                        newDeleteIds.push(file.response.attachmentId)
                        setDeleteIds(newDeleteIds);
                    }
                    resolve(true);

                },
                onCancel: () => {
                    resolve(false)
                },
                okText: "Xác nhận",
                cancelText: "Hủy bỏ",
            })
        })
    }

    const beforeFileUpload = (files: File[]) => {
        let accepted = true;
        Array.from(files).forEach(file => {
            if (!docTypes.includes(file.type)) {
                accepted = false;
                notification.error({
                    message: "Định dạng file không hợp lệ!",
                })
            } else if (file.size / (1024 * 1024) > 5) {
                accepted = false;
                notification.error({
                    message: "File tải lên vượt quá dung lượng 5MB!",
                })
            }
        })

        return accepted || Upload.LIST_IGNORE;
    }

    const normFile = (e: any) => {
        return e.fileList;
    };

    const customUpload = (options: any, type: string) => {
        options.onProgress({ percent: 0 })
        let formData = new FormData();
        formData.append('file', options.file);
        uploadFile({
            file: formData,
            objectType: fileTypes.ACCOUNT,
            storageType: type || "",
        }).unwrap()
            .then(res => {
                options.onProgress({ percent: 100 });
                options.onSuccess(res, options.file);
                if (type === profileIDTypes.ORGANIZATION) {
                    setOrgFile({ id: res.attachmentId, fileName: res.fileName });
                }
            }).catch(err => {
                options.onError();
            })
    }

    const uploadFileAfterSubmit = (id: number) => {
        uploadFileForObject({
            listFileIds: [orgFile.id],
            objectId: id,
            objectType: fileTypes.ACCOUNT
        }).unwrap().then(res => {
            onRegisterSuccess();
        }).catch(err => {
            notification.error({
                message: err.data?.message || "Upload ảnh thất bại!"
            })
        })
    }

    return (
        <Form
            className="custom-form validation-form"
            style={{ marginTop: 15, marginBottom: 45 }}
            name="register"
            form={form}
            colon={false}
            onValuesChange={onValuesChange}
            onFinish={onFinish}
        >
            <Row gutter={30} ref={divRef}>
                <Col span={24}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="orgName"
                        rules={[{ required: true, message: "Vui lòng nhập tên tổ chức.", whitespace: true }]}>
                        <FloatInput label="Tên tổ chức" placeholder="Tên tổ chức" maxLength={100} required />
                    </Form.Item>
                    <label className="input-show-count">{count.orgName || 0}/100</label>
                </Col>
                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="email"
                        rules={[
                            { validator: (_, value) => checkEmptyInput(_, value, "Vui lòng nhập email.") },
                            { required: true, type: 'email', message: "Vui lòng nhập email đúng định dạng." },
                        ]}
                        status={errorList?.email ? "error" : ""}
                        extra={errorList?.email ? <span className="text-danger font-size-12">{errorList.email}</span> : ""}>
                        <FloatInput label="Email tổ chức" placeholder="Email tổ chức" maxLength={50} required autoComplete="off" />
                    </Form.Item>
                    <label className="input-show-count">{count.email || 0}/50</label>
                </Col>

                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="orgPhone"
                        rules={[
                            { required: true, message: "Vui lòng nhập số điện thoại." },
                            { pattern: patterns.PHONE, message: "Số điện thoại không đúng." }
                        ]}
                        status={errorList?.orgPhone ? "error" : ""}
                        extra={errorList?.orgPhone ? <span className="text-danger font-size-12">{errorList.orgPhone}</span> : ""}>
                        <FloatInput label="Số điện thoại" placeholder="Số điện thoại" maxLength={12} required />
                    </Form.Item>
                    <label className="input-show-count">{count.orgPhone || 0}/12</label>
                </Col>

                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="businessRegistrationNumber"
                        rules={[
                            { validator: (_, value) => checkEmptyInput(_, value, "Vui lòng nhập số đăng ký kinh doanh.") },
                            { required: true, message: "Vui lòng nhập số đăng kí kinh doanh." },
                        ]}
                        status={errorList?.businessRegistrationNumber ? "error" : ""}
                        extra={errorList?.businessRegistrationNumber ? <span className="text-danger font-size-12">{errorList.businessRegistrationNumber}</span> : ""}>
                        <FloatInput label="Số ĐKKD" placeholder="Số ĐKKD" maxLength={20} required />
                    </Form.Item>
                    <label className="input-show-count">{count.businessRegistrationNumber || 0}/20</label>
                </Col>

                <Col span={12}>
                    <Form.Item
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                        name="file"
                        rules={[{
                            required: true,
                            message: "Vui lòng nhập file"
                        }]}>
                        <Upload
                            onRemove={(file) => onFileRemove(file)}
                            customRequest={(options) => customUpload(options, profileIDTypes.ORGANIZATION)}
                            accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                                        .pdf,application/pdf"
                            beforeUpload={(file, fileList) => beforeFileUpload(fileList)}
                            name="image"
                            showUploadList={false}
                            maxCount={1}
                            className="user-profile-upload"
                        >
                            <div className="input-wrapper">
                                <div className="text-overflow-custom-one-line">{orgFile?.fileName || <><span>GIẤY PHÉP ĐKKD <sup style={{ color: 'red', fontSize: 11 }}>*</sup></span></>}</div>
                                <div className="upload-btn-wrapper">
                                    <Button style={{ background: "#E59F17", borderRadius: 10, color: 'white' }}>CHỌN TỆP</Button>
                                    <Tooltip title="Định dạng .pdf, .doc, .docx">
                                        <QuestionCircleFilled style={{ color: "#9CA3AF", marginLeft: 5 }} />
                                    </Tooltip>
                                    {/* <input type="file" name="myfile" /> */}
                                </div>
                            </div>

                        </Upload>

                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="name"
                        rules={[{ required: true, message: "Vui lòng nhập người đại diện.", whitespace: true }]}>
                        <FloatInput label="Người đại diện" placeholder="Người đại diện" maxLength={50} required autoComplete="off" />
                    </Form.Item>
                    <label className="input-show-count">{count.name || 0}/50</label>
                </Col>
                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="phone"
                        rules={[
                            { required: true, message: "Vui lòng nhập số điện thoại." },
                            { pattern: patterns.PHONE, message: "Số điện thoại không đúng." }
                        ]}
                        status={errorList?.phone ? "error" : ""}
                        extra={errorList?.phone ? <span className="text-danger font-size-12">{errorList.phone}</span> : ""}>
                        <FloatInput label="Số điện thoại người đại diện" placeholder="Số điện thoại người đại diện" maxLength={12} required autoComplete="off" />
                    </Form.Item>
                    <label className="input-show-count">{count.phone || 0}/12</label>
                </Col>
                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="password"
                        rules={[
                            {
                                required: true,
                                message: "Vui lòng nhập mật khẩu."
                            },
                            {
                                pattern: patterns.PASSWORD,
                                message: "Tối thiểu 8 kí tự, bao gồm chữ hoa, chữ thường, số và ký tự đặc biệt."
                            },]}>
                        <FloatInput label="Mật khẩu" placeholder="Nhập mật khẩu" maxLength={20} isPassword={true} required autoComplete="off" />
                    </Form.Item>
                    <label className="input-show-count">{count.password || 0}/20</label>
                </Col>
                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="confirmPassword"
                        dependencies={["password"]}
                        rules={[
                            {
                                required: true,
                                message: "Vui lòng nhập lại mật khẩu."
                            },
                            {
                                pattern: patterns.PASSWORD,
                                message: "Tối thiểu 8 kí tự, bao gồm chữ hoa, chữ thường, số và ký tự đặc biệt."
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Mật khẩu nhập lại không khớp!'));
                                },
                            }),
                        ]}>
                        <FloatInput label="Nhập lại mật khẩu" placeholder="Nhập lại mật khẩu" maxLength={20} isPassword={true} required />
                    </Form.Item>
                    <label className="input-show-count">{count.confirmPassword || 0}/20</label>
                </Col>

                <Col span={24}>
                    <Form.Item
                        style={{ marginBottom: 20 }}
                        label={<span className="font-size-18">Địa chỉ <sup style={{ color: 'red' }}>*</sup></span>} />
                </Col>
                <Col span={8}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="province"
                        rules={[
                            { required: true, message: "Vui lòng chọn Tỉnh/Thành phố." },
                        ]}
                    >
                        <Select
                            filterSort={(optionA, optionB) =>
                                optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase())
                            }
                            optionFilterProp="label"
                            showSearch
                            placeholder="Chọn Tỉnh/Thành phố"
                            className="custom-select"
                            options={province?.sort((a, b) => helpers.compareListByName(a, b, "provinceName")).map(item => ({
                                label: item.provinceName,
                                value: item.provinceId
                            }))}
                        ></Select>
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="district"
                        rules={[
                            { required: true, message: "Vui lòng chọn Quận/Huyện." },
                        ]}
                    >
                        <Select
                            filterSort={(optionA, optionB) =>
                                optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase())
                            }
                            optionFilterProp="label"
                            showSearch
                            placeholder="Quận/Huyện"
                            className="custom-select"
                            options={districtList?.sort((a, b) => helpers.compareListByName(a, b, "districtName")).map(item => ({
                                label: item.districtName,
                                value: item.districtId
                            }))}
                        ></Select>
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="ward"
                        rules={[
                            { required: true, message: "Vui lòng chọn Phường/Xã." },
                        ]}
                    >
                        <Select
                            filterSort={(optionA, optionB) =>
                                optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase())
                            }
                            optionFilterProp="label"
                            showSearch
                            placeholder="Phường/Xã"
                            className="custom-select"
                            options={wardList?.sort((a, b) => helpers.compareListByName(a, b, "communeName")).map(item => ({
                                label: item.communeName,
                                value: item.communeId
                            }))}
                        >
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="address"
                        rules={[
                            { required: true, message: "Vui lòng nhập địa chỉ chi tiết.", whitespace: true },
                        ]}
                    >
                        <FloatTextArea label="Địa chỉ" placeholder="Nhập địa chỉ số nhà,..." maxLength={200} autoComplete="off" />
                    </Form.Item>
                    <label className="input-show-count">{count.address || 0}/200</label>
                </Col>
            </Row>
            <Row style={{ marginTop: 30 }} align="middle" justify="center">
                <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_KEY || ""}
                    onChange={onValidate}
                    hl="vi"
                />
            </Row>
            <Row style={{ marginTop: 25 }} align="middle" justify="center">
                <Button
                    loading={isLoading}
                    htmlType="submit"
                    className={buttonDisabled ? "button-light-gray" : "button-light-yellow"}
                    style={{ height: 50, width: 240, fontSize: 16 }}
                    disabled={buttonDisabled}>ĐĂNG KÝ</Button>
            </Row>

            <Row style={{ marginTop: 35 }} align="middle" justify="center">
                <span>Bạn đã có tài khoản?<span className="anchor-text" onClick={openLogin}> Đăng nhập ngay</span></span>
            </Row>
        </Form >
    )
}

export default RegisterOrgForm;